import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import AXIOSCONFIGMETHOD from "../../../Axios/axiosConfigMethod";
import { webServiceUrl } from "../../../core/api_utils/webserviceUrl";
import Loading from "../../../core/common/custom-loading/Loading";
import { toast } from "react-toastify";

interface Member {
  employee_id: string;
  organization_user_id: string;
  email_id: string;
  full_name: string;
  mobile_no: number;
}

interface FormData {
  groupName: string;
  description: string;
  selectedMembers: Member[];
  search: string;
}

const CreateGroupPage = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const organizationId = localStorage.getItem("organization_id");

  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState<Member[]>([]);
  const [filtered, setFiltered] = useState<Member[]>([]);
  const [isFocused, setIsFocused] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    groupName: "",
    description: "",
    selectedMembers: [],
    search: "",
  });

  // Fetch sales team members
  const SalesTeamData = async () => {
    const payload = { organization_id: organizationId };
    try {
      setLoading(true);
      const response = await AXIOSCONFIGMETHOD(
        "post",
        webServiceUrl.SALES_TEAM,
        payload,
      );
      if (response?.success) {
        setMembers(response.data || []);
        setFiltered(response.data || []);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    SalesTeamData();
  }, []);

  useEffect(() => {
    const lower = formData.search.toLowerCase();
    setFiltered(
      members.filter((m) => m.full_name.toLowerCase().includes(lower)),
    );
  }, [formData.search, members]);

  const handleCheckboxSelect = (member: Member) => {
    const isSelected = formData.selectedMembers.some(
      (s) => s.organization_user_id === member.organization_user_id,
    );
    setFormData((prev) => ({
      ...prev,
      selectedMembers: isSelected
        ? prev.selectedMembers.filter(
            (s) => s.organization_user_id !== member.organization_user_id,
          )
        : [...prev.selectedMembers, member],
    }));
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const newSelected = [
        ...formData.selectedMembers,
        ...filtered.filter(
          (m) =>
            !formData.selectedMembers.some(
              (s) => s.employee_id === m.employee_id,
            ),
        ),
      ];
      setFormData((prev) => ({ ...prev, selectedMembers: newSelected }));
    } else {
      const remaining = formData.selectedMembers.filter(
        (s) => !filtered.some((m) => m.employee_id === s.employee_id),
      );
      setFormData((prev) => ({ ...prev, selectedMembers: remaining }));
    }
  };

  const handleRemove = (id: string) =>
    setFormData((prev) => ({
      ...prev,
      selectedMembers: prev.selectedMembers.filter((m) => m.employee_id !== id),
    }));

  const handleCreateGroup = async () => {
    if (!formData.groupName.trim()) {
      toast.error("Please enter a group name.");
      return;
    }

    if (!formData.description.trim()) {
      toast.error("Please enter a group description.");
      return;
    }

    if (formData.selectedMembers.length === 0) {
      toast.error("Please select at least one member.");
      return;
    }

    const payload = {
      group_name: formData.groupName,
      organization_id: organizationId,
      group_description: formData.description,
      team_members: formData.selectedMembers.map((m) => m.organization_user_id),
    };

    try {
      setLoading(true);
      const response = await AXIOSCONFIGMETHOD(
        "post",
        webServiceUrl.CREATE_GROUPS,
        payload,
      );

      if (response?.success) {
        toast.success("Group created successfully!");
        // Reset form
        setFormData({
          groupName: "",
          description: "",
          selectedMembers: [],
          search: "",
        });
        navigate(routes.ManageSalesGroup);
      } else {
        toast.error(response?.message || "Failed to create group.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong while creating the group.");
    } finally {
      setLoading(false);
    }

    // console.log("Payload to submit:", payload);
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Breadcrumb */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Create Sales Group</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Dashboard</li>
                <li className="breadcrumb-item">Group Management</li>
                <li className="breadcrumb-item active" aria-current="page">
                  Create Sales Group
                </li>
              </ol>
            </nav>
          </div>
          <button
            className="btn btn-outline-secondary"
            onClick={() => navigate(-1)}
          >
            <i className="ti ti-arrow-left me-2" />
            Back
          </button>
        </div>

        {/* Create Group Card */}
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <h5>Create New Group</h5>
          </div>

          <div className="card-body">
            {loading ? (
              <Loading />
            ) : (
              <form>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Group Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter group name"
                    value={formData.groupName}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        groupName: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Description</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Enter group description"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                  ></textarea>
                </div>

                {/* Add Members */}
                <div className="mb-3 position-relative">
                  <label className="form-label fw-semibold">Add Members</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search and add members..."
                    value={formData.search}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        search: e.target.value,
                      }))
                    }
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setTimeout(() => setIsFocused(false), 150)}
                  />

                  {/* Selected members badges */}
                  <div className="mt-2 mb-4">
                    {formData.selectedMembers.length !== 0 && (
                      <p
                        className="mb-2 fw-semibold"
                        style={{ fontSize: "13px" }}
                      >
                        Selected Members
                      </p>
                    )}

                    <div className="d-flex flex-wrap gap-2">
                      {formData.selectedMembers.map((m) => (
                        <span
                          key={m.employee_id}
                          className="badge bg-primary d-flex align-items-center"
                        >
                          {m.full_name}
                          <button
                            type="button"
                            className="btn-close btn-close-white ms-2"
                            style={{ fontSize: "0.6rem" }}
                            onClick={() => handleRemove(m.employee_id)}
                          ></button>
                        </span>
                      ))}
                    </div>
                  </div>

                  {(isFocused || formData.search) && (
                    <div
                      className="dropdown-table-container"
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <table className="table table-sm mb-0 align-middle">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: "40px" }}>
                              <input
                                type="checkbox"
                                checked={
                                  filtered.length > 0 &&
                                  filtered.every((m) =>
                                    formData.selectedMembers.some(
                                      (s) => s.employee_id === m.employee_id,
                                    ),
                                  )
                                }
                                onChange={handleSelectAll}
                              />
                            </th>
                            <th>Employee ID</th>
                            <th>Name</th>
                            <th>Phone</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filtered.length > 0 ? (
                            filtered.map((m) => {
                              const isChecked = formData.selectedMembers.some(
                                (s) => s.employee_id === m.employee_id,
                              );
                              return (
                                <tr
                                  key={m.employee_id}
                                  className={isChecked ? "table-primary" : ""}
                                >
                                  <td>
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() => handleCheckboxSelect(m)}
                                    />
                                  </td>
                                  <td>{m.organization_user_id}</td>
                                  <td>{m.full_name}</td>
                                  <td>{m.mobile_no || "-"}</td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td
                                colSpan={4}
                                className="text-center text-muted py-2"
                              >
                                No results found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleCreateGroup}
                    disabled={loading}
                  >
                    {loading ? "Creating..." : "Create Group"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
        <p className="mb-0">
          © {new Date().getFullYear()} Nestfin Technology. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default CreateGroupPage;
